import React from 'react';
import { cn } from '@/lib/utils';

/**
 * MobileRecordCard — compact, structured replacement for a desktop table row on
 * narrow screens. Presentational only: the caller passes the real image node,
 * the real status badge and the real action menu so permissions, routing and
 * saved imagery are preserved exactly.
 *
 * Props:
 *  - image: ReactNode (thumbnail / logo with its existing placeholder logic)
 *  - title: string, onTitleClick?: fn, titleTestid?: string
 *  - meta: [{ label, value, full? }]  (empty/nullish values are skipped)
 *  - badge: ReactNode (e.g. a StatusPill)
 *  - actions: ReactNode (e.g. the existing DropdownMenu)
 *  - onClick?: fn (open detail)
 */
export const MobileRecordCard = ({
  image, title, onTitleClick, titleTestid, meta = [], badge, actions, onClick, className, testid, children,
}) => (
  <div
    data-testid={testid}
    onClick={onClick}
    className={cn(
      'flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-[0_1px_2px_rgba(16,24,40,0.05)]',
      onClick && 'cursor-pointer active:bg-slate-50',
      className,
    )}
  >
    {image && <div className="shrink-0">{image}</div>}
    <div className="min-w-0 flex-1">
      <div className="flex items-start justify-between gap-2">
        {onTitleClick ? (
          <button
            type="button"
            data-testid={titleTestid}
            onClick={(e) => { e.stopPropagation(); onTitleClick(); }}
            className="min-w-0 break-words text-left text-sm font-bold leading-tight text-[#0E2A47] hover:underline"
          >
            {title}
          </button>
        ) : (
          <div className="min-w-0 break-words text-sm font-bold leading-tight text-[#0E2A47]">{title}</div>
        )}
        {badge && <div className="shrink-0">{badge}</div>}
      </div>
      {meta.length > 0 && (
        <dl className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-1">
          {meta
            .filter((m) => m && m.value !== undefined && m.value !== null && m.value !== '')
            .map((m, i) => (
              <div key={i} className={cn('min-w-0', m.full && 'col-span-2')}>
                <dt className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{m.label}</dt>
                <dd className="truncate text-xs text-slate-700">{m.value}</dd>
              </div>
            ))}
        </dl>
      )}
      {children}
    </div>
    {actions && <div className="shrink-0" onClick={(e) => e.stopPropagation()}>{actions}</div>}
  </div>
);

export const MobileRecordList = ({ children, className, testid = 'mobile-record-list' }) => (
  <div data-testid={testid} className={cn('space-y-2.5', className)}>{children}</div>
);

export default MobileRecordCard;
